/**
 * Every label, version and length of the formats Bridge makes, in one place (docs/FORMAT.md is the written form). A change
 * here is a change of the protocol: it needs a new version (docs/FORMAT.md, API.md, ENCRYPTION.md, POST-QUANTUM.md,
 * ADR 0005) and, because the server checks the same shapes in `server/src/exchangeKeys.ts`, `web/test/labels.test.ts`
 * compares the two.
 */

// ------------------------------------------------------------------ envelope

/** The version of every format below; a reader refuses another. */
export const VERSION = 3;
/** AES-256-GCM with keys derived from the link key by HKDF-SHA-256. 0x02 is reserved. */
export const SUITE_AES_GCM = 1;

export const FLAG_LAST_CHUNK = 0x01;
export const FLAG_PADDED = 0x02;
export const FLAG_DELETION = 0x04;
export const FLAGS_KNOWN = FLAG_LAST_CHUNK | FLAG_PADDED | FLAG_DELETION;

export const IV_BYTES = 12;
export const TAG_BYTES = 16;
export const KEY_BITS = 256;
export const KEY_BYTES = KEY_BITS / 8;
/** version, suite, flags. */
export const ENVELOPE_HEAD_BYTES = 3;
/** A device's random name on a bridge, and the width of its counter. */
export const SENDER_BYTES = 8;
export const SEQ_BYTES = 4;
/** version ‖ suite ‖ flags ‖ sender ‖ seq ‖ iv, in front of a message's ciphertext. */
export const MESSAGE_HEAD_BYTES = ENVELOPE_HEAD_BYTES + SENDER_BYTES + SEQ_BYTES + IV_BYTES;
/** What a chunk adds to its plaintext: the three head bytes and the tag. */
export const CHUNK_OVERHEAD = ENVELOPE_HEAD_BYTES + TAG_BYTES;

// ------------------------------------------------------------------- padding

/** Plaintext is padded to the first of these that fits (after its 4-byte length), then to a Padmé length. */
export const PAD_BUCKETS: readonly number[] = [256, 1024, 4096];
export const PAD_LENGTH_BYTES = 4;

// ----------------------------------------------- messages, subkeys and chunks

/** HKDF `info` of the key for texts, file headers and deletion marks. */
export const MESSAGE_KEY_INFO = 'passwork-bridge/v3/key/message';
/** HKDF `info` of the key of one file: this, a zero byte and the file's nonce text. */
export const FILE_KEY_INFO = 'passwork-bridge/v3/key/file';
/** Additional data of a message and of a chunk: the label, a zero byte, then the fields (docs/FORMAT.md §2). */
export const MESSAGE_LABEL = 'passwork-bridge/message';
export const FILE_CHUNK_LABEL = 'passwork-bridge/file';

export const ROLE_BYTE = { owner: 1, guest: 2 } as const;
export const PART_TEXT = 1;
export const PART_FILE_HEADER = 2;
export const PART_DELETION = 3;

/** A message id is chosen by the sender: 16 random bytes, 22 base64url characters. */
export const MESSAGE_ID_BYTES = 16;
export const MESSAGE_ID_PATTERN = /^[A-Za-z0-9_-]{22}$/;
/** A file's random nonce, bound into every chunk and its key: 16 random bytes, 22 base64url characters. */
export const FILE_NONCE_BYTES = 16;
export const FILE_NONCE_PATTERN = /^[A-Za-z0-9_-]{22}$/;
/** The longest file name shown or saved, in characters. */
export const FILE_NAME_MAX = 255;

// ------------------------------------------------------------ hybrid exchange

export type ExchangeFlow = 'connect' | 'nearby';

/** Version and suite lead every public key and wrapped key (docs/FORMAT.md §7). */
export const EXCHANGE_VERSION = VERSION;
export const EXCHANGE_SUITE = SUITE_AES_GCM;
export const EXCHANGE_LABEL = 'passwork-bridge/hybrid/v3';
export const FLOW_BYTE: Record<ExchangeFlow, number> = { connect: 1, nearby: 2 };

export const P256_POINT_BYTES = 65;
/** Version ‖ suite ‖ uncompressed P-256 point. */
export const OWNER_KEY_BYTES = 2 + P256_POINT_BYTES;
// FIPS 203, ML-KEM-768 (a test compares them with the library's own)
export const KEM_PUBLIC_KEY_BYTES = 1184;
export const KEM_CIPHERTEXT_BYTES = 1088;
/** Version ‖ suite ‖ uncompressed P-256 point ‖ ML-KEM-768 encapsulation key. */
export const GUEST_KEY_BYTES = OWNER_KEY_BYTES + KEM_PUBLIC_KEY_BYTES;
/** Version ‖ suite ‖ ML-KEM ciphertext ‖ IV ‖ AES-GCM output (the link key and the tag). */
export const WRAPPED_KEY_BYTES = 2 + KEM_CIPHERTEXT_BYTES + IV_BYTES + KEY_BYTES + TAG_BYTES;

// ------------------------------------------------------------------- pairing

/** One label per flow, so a commitment or a comparison made for one cannot be passed off in the other. */
export const COMMIT_LABEL: Record<ExchangeFlow, string> = { nearby: 'passwork-bridge/nearby/commit/v2', connect: 'passwork-bridge/connect/commit/v2' };
export const SAS_LABEL: Record<ExchangeFlow, string> = { nearby: 'passwork-bridge/nearby/sas/v2', connect: 'passwork-bridge/connect/sas/v2' };
/** Emoji shown for a comparison, and the bits each one carries (a table of 64): 24 bits in all. */
export const SAS_COUNT = 4;
export const SAS_BITS_EACH = 6;

// ---------------------------------------------------- identifiers and codes

/** The letters a connect code is made of: no I, O, 0 or 1, which are easy to misread (the server's `ids.ts` agrees). */
export const CONNECT_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
export const CONNECT_CODE_LENGTH = 6;
/** 64 symbols, so a byte modulo its length is unbiased. */
export const ID_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
